import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { cn } from '@/lib/utils';
import type { LeaveDetail } from '../../../types/employee.types';
import { useLeaveCategoriesLookup } from '../../../hooks/use-leave-lookups';
import { HrInfoValues } from '../../../schemas/employee-form-schema';
import { canBeDefaultLeave, withDefaultLeave } from '../../../schemas/hr-term-schema';
import { computeAvgDailyWorkHour, latestScheduleOf } from '../../../schemas/working-info-schema';
import { FormSection } from '../form-layout';

const toHours = (days: number, averageDailyHours: number) =>
  Number.isFinite(days) ? Number((days * averageDailyHours).toFixed(2)) : 0;

/** Days as typed in a number input; an empty input stays NaN so validation can report it as required. */
const parseDays = (event: React.ChangeEvent<HTMLInputElement>) => event.target.valueAsNumber;

/**
 * One table row. On phones the label takes a row of its own and the two numbers sit side by side under
 * it (with a caption instead of the column header); from `sm` up it is the usual three-column table row.
 */
const LEAVE_ROW =
  'grid grid-cols-2 items-start gap-x-3 gap-y-2 px-4 py-3 text-sm sm:grid-cols-[minmax(0,2fr)_1fr_1fr] sm:items-center';

/** Column caption that only shows on phones, where the table header row is hidden. */
const MobileCaption = ({ children }: { children: React.ReactNode }) => (
  <span className="block text-xs text-muted-foreground sm:hidden">{children}</span>
);

/**
 * Leave allotment of one HR term: the leave categories of the active organization (tick
 * the ones that apply, adjust the days, pick the default leave that TOIL and missed attendance count
 * against) and the leave / TOIL days carried forward into the term.
 */
export const LeaveAllotmentSection = ({ termIndex }: { termIndex: number }) => {
  const { t } = useTranslation();
  const { control, watch, setValue, formState } = useFormContext<HrInfoValues>();

  const { categories, isLoading } = useLeaveCategoriesLookup();

  const details = watch(`HrTerms.${termIndex}.LeaveDetails`) ?? [];
  const employmentType = watch(`HrTerms.${termIndex}.EmploymentType`);
  const defaultLeaveId = details.find(
    (detail) => detail.IsDefault && canBeDefaultLeave(detail)
  )?.LeaveCategoryId;
  // The hours of a carried-forward day: the work day the term ends with (its AvgDailyWorkHour).
  const latestSchedule = latestScheduleOf(watch(`HrTerms.${termIndex}.WorkSchedules`));
  const averageDailyHours = computeAvgDailyWorkHour(
    latestSchedule?.WorkDays,
    latestSchedule?.WorkHours
  );

  const appliedCount = details.filter((detail) => detail.IsApplicable).length;
  const leaveError = formState.errors.HrTerms?.[termIndex]?.LeaveDetails as
    { message?: string; root?: { message?: string } } | undefined;
  const leaveErrorMessage = leaveError?.message ?? leaveError?.root?.message;

  /** Ticking or unticking a category keeps the term's default leave on a ticked one. */
  const setDetails = (next: LeaveDetail[]) =>
    setValue(`HrTerms.${termIndex}.LeaveDetails`, withDefaultLeave(next, employmentType), {
      shouldDirty: true,
      shouldValidate: formState.isSubmitted,
    });

  const handleSelectAll = (checked: boolean) =>
    setDetails(details.map((detail) => ({ ...detail, IsApplicable: checked })));

  const handleApplicableChange = (index: number, checked: boolean) =>
    setDetails(
      details.map((detail, i) => (i === index ? { ...detail, IsApplicable: checked } : detail))
    );

  const handleDefaultChange = (leaveCategoryId: string) =>
    setValue(
      `HrTerms.${termIndex}.LeaveDetails`,
      details.map((detail) => ({
        ...detail,
        IsDefault: detail.LeaveCategoryId === leaveCategoryId,
      })),
      { shouldDirty: true, shouldValidate: formState.isSubmitted }
    );

  const carryForwardRows = [
    { name: 'LeaveCarryForwardDays', label: t('LEAVE') },
    { name: 'ToilCarryForwardDays', label: t('TOIL') },
    { name: 'SpecialToilCarryForwardDays', label: t('SPECIAL_TOIL') },
  ] as const;

  return (
    <div className="flex flex-col gap-8">
      <FormSection title={t('LEAVE_ALLOTMENT')} required>
        {!isLoading && categories?.length === 0 && (
          <p className="text-[0.8rem] font-medium text-destructive">
            {t('NO_LEAVE_CATEGORIES_IN_ORGANIZATION')}
          </p>
        )}

        {/* The rows are built from the organization's leave categories: placeholders until they load. */}
        {isLoading && details.length === 0 && (
          <div
            className="flex flex-col gap-0 divide-y overflow-hidden rounded-lg border"
            aria-busy="true"
          >
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="size-4 shrink-0" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="ml-auto h-8 w-20" />
              </div>
            ))}
          </div>
        )}

        {details.length > 0 && (
          <RadioGroup
            value={defaultLeaveId ?? ''}
            onValueChange={handleDefaultChange}
            aria-label={t('DEFAULT_LEAVE')}
            className="flex flex-col gap-0 divide-y overflow-hidden rounded-lg border"
          >
            <div className={cn(LEAVE_ROW, 'bg-muted/40 py-2.5 font-semibold')}>
              <label className="col-span-2 flex items-center gap-3 sm:col-span-1">
                <Checkbox
                  checked={
                    appliedCount === details.length
                      ? true
                      : appliedCount > 0
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={(checked) => handleSelectAll(checked === true)}
                  aria-label={t('LEAVE_TYPE')}
                />
                {t('LEAVE_TYPE')}
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  {t('DEFAULT_LEAVE')}
                </span>
              </label>
              <span className="hidden sm:block">{t('DAYS')}</span>
              <span className="hidden sm:block">{t('AFFILIATED_HOURS')}</span>
            </div>

            {details.map((detail, index) => (
              <div key={detail.LeaveCategoryId} className={LEAVE_ROW}>
                <FormField
                  control={control}
                  name={`HrTerms.${termIndex}.LeaveDetails.${index}.IsApplicable`}
                  render={({ field }) => (
                    <FormItem className="col-span-2 flex flex-row items-center gap-3 space-y-0 sm:col-span-1">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) =>
                            handleApplicableChange(index, checked === true)
                          }
                        />
                      </FormControl>
                      <FormLabel className="font-normal">{detail.LeaveCategory}</FormLabel>
                      <RadioGroupItem
                        value={detail.LeaveCategoryId}
                        disabled={!canBeDefaultLeave(detail)}
                        className="ml-auto"
                        aria-label={`${detail.LeaveCategory} ${t('DEFAULT_LEAVE')}`}
                      />
                    </FormItem>
                  )}
                />

                {detail.IsUnlimited ? (
                  <span className="col-span-2 pl-7 text-muted-foreground sm:pl-0">
                    {t('UNLIMITED')}
                  </span>
                ) : (
                  <>
                    <FormField
                      control={control}
                      name={`HrTerms.${termIndex}.LeaveDetails.${index}.LeaveLimitTotalDays`}
                      render={({ field }) => (
                        <FormItem>
                          <MobileCaption>{t('DAYS')}</MobileCaption>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              step="any"
                              {...field}
                              value={Number.isNaN(field.value) ? '' : field.value}
                              onChange={(event) => field.onChange(parseDays(event))}
                              aria-label={`${detail.LeaveCategory} ${t('DAYS')}`}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="space-y-1">
                      <MobileCaption>{t('AFFILIATED_HOURS')}</MobileCaption>
                      <Input
                        readOnly
                        disabled
                        value={toHours(detail.LeaveLimitTotalDays, averageDailyHours)}
                        aria-label={`${detail.LeaveCategory} ${t('AFFILIATED_HOURS')}`}
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </RadioGroup>
        )}

        {leaveErrorMessage && (
          <p className="text-[0.8rem] font-medium text-destructive">{leaveErrorMessage}</p>
        )}
      </FormSection>

      <FormSection title={`${t('LEAVE_CARRY_FORWARD')} / ${t('TOIL_CARRY_FORWARD')}`} required>
        <div className="flex flex-col divide-y overflow-hidden rounded-lg border">
          <div className={cn(LEAVE_ROW, 'hidden bg-muted/40 py-2.5 font-semibold sm:grid')}>
            <span>{t('LEAVE_TYPE')}</span>
            <span>{t('DAYS')}</span>
            <span>{t('AFFILIATED_HOURS')}</span>
          </div>
          {carryForwardRows.map((row) => (
            <div key={row.name} className={LEAVE_ROW}>
              <Label className="col-span-2 sm:col-span-1">{row.label}</Label>
              <FormField
                control={control}
                name={`HrTerms.${termIndex}.${row.name}`}
                render={({ field }) => (
                  <FormItem>
                    <MobileCaption>{t('DAYS')}</MobileCaption>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="any"
                        {...field}
                        value={Number.isNaN(field.value) ? '' : field.value}
                        onChange={(event) => field.onChange(parseDays(event))}
                        aria-label={`${row.label} ${t('DAYS')}`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-1">
                <MobileCaption>{t('AFFILIATED_HOURS')}</MobileCaption>
                <Input
                  readOnly
                  disabled
                  value={toHours(watch(`HrTerms.${termIndex}.${row.name}`), averageDailyHours)}
                  aria-label={`${row.label} ${t('AFFILIATED_HOURS')}`}
                />
              </div>
            </div>
          ))}
        </div>
      </FormSection>
    </div>
  );
};
